import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Smartphone,
  Mail,
  Lock,
  Check,
  AlertCircle,
  KeyRound,
  Copy,
} from "lucide-react";

/* ──────────────────────────────────────────────────────────────
   LOCKED — auth storage keys. These MUST be identical to the keys
   read by Dashboard.jsx / Campaigns.jsx / ProtectedRoute.jsx etc.
   If the rest of the app reads `token` instead of `martech_token`,
   change TOKEN_KEY here to "token" and nothing else.
   ────────────────────────────────────────────────────────────── */
const TOKEN_KEY = "martech_token";
const ROLE_KEY = "role";
const TENANT_KEY = "tenant_id";
const TOTP_VERIFIED_KEY = "martech_totp_verified";
const KEEP_SIGNED_IN_KEY = "keep_signed_in";

/* LOCKED — relative paths only. On Cloudflare these need a proxy
   (a _redirects rule or Pages Function) routing /api/* to the API. */
const API = {
  login: "https://api.martechlabs.in/api/auth/login",
  totpSetup: "https://api.martechlabs.in/api/auth/totp/setup",
  totpVerify: "https://api.martechlabs.in/api/auth/totp/verify",
  sessionCheck: "https://api.martechlabs.in/api/auth/totp/session-check",
  forgot: "https://api.martechlabs.in/api/auth/forgot-password",
};

const FONT_HREF =
  "https://api.fontshare.com/v2/css?f[]=general-sans@400,500,600,700&display=swap";

const FEATURES = [
  ["Unified profiles", "Every customer in one real-time view"],
  ["Omnichannel journeys", "WhatsApp, Email & SMS in one flow"],
  ["DPDP-compliant", "Privacy-first, compliant by default"],
  ["Live segments", "Audiences that update instantly"],
];

// Inject fonts as early as possible so desktop and mobile render the same
// typeface. Runs once at import time; the effect below is just a guard.
const injectFonts = () => {
  if (typeof document === "undefined" || document.getElementById("auth-fonts"))
    return;
  const pre = document.createElement("link");
  pre.rel = "preconnect";
  pre.href = "https://api.fontshare.com";
  pre.crossOrigin = "anonymous";
  document.head.appendChild(pre);
  const link = document.createElement("link");
  link.id = "auth-fonts";
  link.rel = "stylesheet";
  link.href = FONT_HREF;
  document.head.appendChild(link);
};
injectFonts();

const post = async (url, body, token) => {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body || {}),
    });
    let data = {};
    try {
      data = await res.json();
    } catch (e) {
      data = {};
    }
    return { ok: res.ok, status: res.status, data, networkError: false };
  } catch (e) {
    return { ok: false, status: 0, data: {}, networkError: true };
  }
};

const reachError = ({ status, networkError }) => {
  if (networkError || status === 0)
    return "Can't reach the server. The /api proxy may not be set up on Cloudflare yet.";
  if (status === 404)
    return "Login service not found (/api isn't routed). Check the Cloudflare proxy.";
  if (status >= 500)
    return "The login service is temporarily unavailable. Try again shortly.";
  return null;
};

export default function Login() {
  const navigate = useNavigate();

  const [screen, setScreen] = useState("booting"); // booting|login|setup|verify|forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [userId, setUserId] = useState(null);
  const [pending, setPending] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [setupStep, setSetupStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    injectFonts();
  }, []);

  const persistAuth = useCallback((auth, keep) => {
    localStorage.setItem(TOKEN_KEY, auth.token || "");
    if (auth.role) localStorage.setItem(ROLE_KEY, auth.role);
    if (auth.tenant_id != null)
      localStorage.setItem(TENANT_KEY, String(auth.tenant_id));
    localStorage.setItem(KEEP_SIGNED_IN_KEY, keep ? "true" : "false");
  }, []);

  useEffect(() => {
    let alive = true;
    const existing = localStorage.getItem(TOKEN_KEY);
    if (!existing) {
      setScreen("login");
      return;
    }
    (async () => {
      const res = await post(API.sessionCheck, {}, existing);
      if (!alive) return;
      if (res.ok && res.data.verified) {
        navigate("/dashboard");
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOTP_VERIFIED_KEY);
        setScreen("login");
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  const handleLogin = async () => {
    setError("");
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setLoading(true);
    const res = await post(API.login, { email: email.trim(), password });
    setLoading(false);

    const reach = reachError(res);
    if (reach) {
      setError(reach);
      return;
    }
    const { ok, data } = res;
    if (!ok || data.success === false) {
      setError(data.message || "Invalid email or password.");
      return;
    }

    const auth = { token: data.token, role: data.role, tenant_id: data.tenant_id };
    if (data.needs_totp_setup) {
      setUserId(data.user_id);
      setPending(auth);
      setSetupStep(1);
      setScreen("setup");
      return;
    }
    if (data.needs_totp_verify) {
      setUserId(data.user_id);
      setPending(auth);
      setScreen("verify");
      return;
    }
    persistAuth(auth, keepSignedIn);
    localStorage.setItem(TOTP_VERIFIED_KEY, "true");
    navigate("/dashboard");
  };

  useEffect(() => {
    if (screen !== "setup" || !userId) return;
    let alive = true;
    setError("");
    setQrCode("");
    setSecret("");
    (async () => {
      const res = await post(API.totpSetup, {
        user_id: userId,
        email: email.trim(),
      });
      if (!alive) return;
      const reach = reachError(res);
      if (reach) {
        setError(reach);
        return;
      }
      if (res.ok && res.data.success !== false) {
        setQrCode(res.data.qr_code || "");
        setSecret(res.data.secret || "");
      } else {
        setError(res.data.message || "Could not start authenticator setup.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [screen, userId, email]);

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };

  const handleVerify = async (code, setupMode) => {
    setError("");
    if (!code || code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setLoading(true);
    const res = await post(API.totpVerify, {
      user_id: userId,
      token: code,
      setup_mode: !!setupMode,
    });
    setLoading(false);
    const reach = reachError(res);
    if (reach) {
      setError(reach);
      return;
    }
    if (res.ok && res.data.verified) {
      persistAuth(pending || {}, keepSignedIn);
      localStorage.setItem(TOTP_VERIFIED_KEY, "true");
      navigate("/dashboard");
    } else {
      setError(res.data.message || "Incorrect code. Try again.");
    }
  };

  const handleForgot = async () => {
    setError("");
    if (!email.trim()) {
      setError("Enter the email linked to your account.");
      return;
    }
    setLoading(true);
    const res = await post(API.forgot, { email: email.trim() });
    setLoading(false);
    const reach = reachError(res);
    if (reach) {
      setError(reach);
      return;
    }
    if (res.ok && res.data.success !== false) {
      setForgotSent(true);
    } else {
      setError(res.data.message || "Could not send reset instructions.");
    }
  };

  if (screen === "booting") {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-3 py-10 text-neutral-500">
          <Loader2 size={18} className="animate-spin" />
          <span>Restoring your session…</span>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {error && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {screen === "login" && (
        <LoginForm
          {...{
            email,
            setEmail,
            password,
            setPassword,
            showPwd,
            setShowPwd,
            keepSignedIn,
            setKeepSignedIn,
            loading,
            handleLogin,
            goForgot: () => {
              setError("");
              setForgotSent(false);
              setScreen("forgot");
            },
          }}
        />
      )}

      {screen === "setup" && (
        <TotpSetup
          {...{
            qrCode,
            secret,
            setupStep,
            setSetupStep,
            copied,
            copySecret,
            loading,
            onVerify: (code) => handleVerify(code, true),
            onBack: () => {
              setError("");
              setScreen("login");
            },
          }}
        />
      )}

      {screen === "verify" && (
        <TotpVerify
          {...{
            loading,
            onVerify: (code) => handleVerify(code, false),
            onBack: () => {
              setError("");
              setScreen("login");
            },
          }}
        />
      )}

      {screen === "forgot" && (
        <Forgot
          {...{
            email,
            setEmail,
            loading,
            forgotSent,
            handleForgot,
            onBack: () => {
              setError("");
              setScreen("login");
            },
          }}
        />
      )}
    </Shell>
  );
}

/* ════════════════════════ layout shell ════════════════════════ */
function Shell({ children }) {
  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-white text-neutral-900"
      style={{
        "--font-display":
          "'General Sans', ui-sans-serif, system-ui, sans-serif",
        "--font-body": "'General Sans', ui-sans-serif, system-ui, sans-serif",
        fontFamily: "var(--font-body)",
      }}
    >
      <style>{`
        @keyframes authRise {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .auth-rise { animation: none !important; }
        }
      `}</style>
      {/* soft pastel atmosphere */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(50% 48% at 12% 8%, rgba(124,92,255,0.12), transparent 60%)," +
            "radial-gradient(42% 42% at 96% 6%, rgba(236,72,153,0.07), transparent 58%)," +
            "radial-gradient(55% 55% at 90% 96%, rgba(99,102,241,0.09), transparent 62%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,15,25,.04) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(15,15,25,.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage:
            "radial-gradient(120% 90% at 30% 20%, #000 30%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(120% 90% at 30% 20%, #000 30%, transparent 80%)",
        }}
      />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-10 sm:px-8 lg:px-14">
        <div className="grid w-full max-w-6xl grid-cols-1 items-center gap-10 lg:grid-cols-[1fr_minmax(380px,420px)] lg:gap-16">
          {/* ── brand column ── */}
          <section
            className="auth-rise order-1 lg:max-w-xl"
            style={{ animation: "authRise 0.6s ease-out both" }}
          >
            <div className="mb-7 flex items-center gap-2.5">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg shadow-violet-500/25">
                <ShieldCheck size={18} className="text-white" />
              </div>
              <span
                className="text-lg font-semibold text-neutral-900"
                style={{
                  fontFamily: "var(--font-display)",
                  letterSpacing: "0.01em",
                }}
              >
                Martech&nbsp;CDP
              </span>
            </div>

            <h1
              className="text-[2rem] font-semibold leading-[1.1] text-neutral-900 sm:text-[2.6rem] lg:text-[3rem] lg:leading-[1.06] xl:text-[3.25rem]"
              style={{
                fontFamily: "var(--font-display)",
                letterSpacing: "-0.015em",
              }}
            >
              Know your customers.
              <br />
              <span className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-500 bg-clip-text text-transparent">
                Grow your revenue.
              </span>
            </h1>

            <ul className="mt-7 space-y-3.5">
              {FEATURES.map(([title, sub]) => (
                <li key={title} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-violet-100 ring-1 ring-violet-200">
                    <Check size={12} className="text-violet-600" />
                  </span>
                  <span className="leading-snug">
                    <span className="text-[15px] font-semibold text-neutral-900">
                      {title}
                    </span>
                    <span className="text-[15px] text-neutral-500">
                      {" "}
                      — {sub}
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-8 hidden text-xs text-neutral-400 lg:block">
              © {new Date().getFullYear()} Martech CDP · Secured with 2-factor
              authentication
            </p>
          </section>

          {/* ── form column ── */}
          <section
            className="auth-rise order-2 w-full lg:justify-self-end"
            style={{ animation: "authRise 0.6s ease-out 0.12s both" }}
          >
            <div className="w-full rounded-2xl border border-neutral-200 bg-white p-7 sm:p-8 lg:p-9 shadow-[0_24px_60px_-28px_rgba(80,40,160,0.30)]">
              {children}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════ shared bits ════════════════════════ */
function Field({ icon: Icon, ...props }) {
  return (
    <div className="group relative">
      <Icon
        size={17}
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors group-focus-within:text-violet-500"
      />
      <input
        {...props}
        className="w-full rounded-xl border border-neutral-200 bg-white py-3 pl-11 pr-11 text-[15px] text-neutral-900 placeholder-neutral-400 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20"
      />
    </div>
  );
}

function PrimaryBtn({ loading, children, ...props }) {
  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 py-3 text-[15px] font-semibold text-white shadow-lg shadow-violet-500/25 transition hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {loading ? <Loader2 size={18} className="animate-spin" /> : children}
    </button>
  );
}

function H({ title, sub }) {
  return (
    <div className="mb-6">
      <h2
        className="text-[1.4rem] font-semibold tracking-tight text-neutral-900"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {title}
      </h2>
      {sub && <p className="mt-1.5 text-sm text-neutral-500">{sub}</p>}
    </div>
  );
}

/* ════════════════════════ screen 1 ════════════════════════ */
function LoginForm({
  email,
  setEmail,
  password,
  setPassword,
  showPwd,
  setShowPwd,
  keepSignedIn,
  setKeepSignedIn,
  loading,
  handleLogin,
  goForgot,
}) {
  const onKey = (e) => e.key === "Enter" && handleLogin();
  return (
    <>
      <H title="Welcome back" sub="Sign in to your Martech CDP workspace." />
      <div className="space-y-3.5">
        <Field
          icon={Mail}
          type="email"
          autoComplete="username"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKey}
        />
        <div className="relative">
          <Field
            icon={Lock}
            type={showPwd ? "text" : "password"}
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKey}
          />
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors hover:text-neutral-700"
            aria-label={showPwd ? "Hide password" : "Show password"}
          >
            {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>

        <div className="flex items-center justify-between pt-0.5">
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-neutral-600">
            <button
              type="button"
              role="checkbox"
              aria-checked={keepSignedIn}
              onClick={() => setKeepSignedIn((v) => !v)}
              className={`grid h-[18px] w-[18px] place-items-center rounded-[6px] border transition ${
                keepSignedIn
                  ? "border-violet-500 bg-violet-600"
                  : "border-neutral-300 bg-white"
              }`}
            >
              {keepSignedIn && <Check size={12} className="text-white" />}
            </button>
            Keep me signed in
          </label>
          <button
            type="button"
            onClick={goForgot}
            className="text-sm font-medium text-violet-600 transition-colors hover:text-violet-700"
          >
            Forgot password?
          </button>
        </div>

        <PrimaryBtn loading={loading} onClick={handleLogin}>
          Sign in <ArrowRight size={17} />
        </PrimaryBtn>
      </div>
    </>
  );
}

/* ════════════════════════ screen 2 — setup ════════════════════════ */
function TotpSetup({
  qrCode,
  secret,
  setupStep,
  setSetupStep,
  copied,
  copySecret,
  loading,
  onVerify,
  onBack,
}) {
  return (
    <>
      <BackLink onBack={onBack} />
      <H
        title="Set up 2-factor auth"
        sub="Protect your account with Google Authenticator."
      />
      <Steps active={setupStep} />

      {setupStep === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-neutral-500">
            Install Google Authenticator on your phone, then tap Next.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <StoreCard label="App Store" sub="iPhone" />
            <StoreCard label="Google Play" sub="Android" />
          </div>
          <PrimaryBtn onClick={() => setSetupStep(2)}>
            Next <ArrowRight size={17} />
          </PrimaryBtn>
        </div>
      )}

      {setupStep === 2 && (
        <div className="space-y-4">
          <p className="text-sm text-neutral-500">
            Scan this QR code inside the app.
          </p>
          <div className="mx-auto grid h-[200px] w-[200px] place-items-center rounded-2xl border border-neutral-200 bg-white p-3">
            {qrCode ? (
              <img
                src={qrCode}
                alt="Authenticator QR code"
                className="h-full w-full object-contain"
              />
            ) : (
              <Loader2 size={22} className="animate-spin text-neutral-400" />
            )}
          </div>

          {secret && (
            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
              <p className="mb-1.5 text-xs text-neutral-500">
                Can&apos;t scan? Enter this key manually:
              </p>
              <div className="flex items-center justify-between gap-2">
                <code className="break-all text-sm tracking-wide text-violet-700">
                  {secret}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  className="shrink-0 rounded-lg border border-neutral-200 p-2 text-neutral-500 transition hover:text-neutral-900"
                  aria-label="Copy setup key"
                >
                  {copied ? (
                    <Check size={15} className="text-emerald-500" />
                  ) : (
                    <Copy size={15} />
                  )}
                </button>
              </div>
            </div>
          )}

          <PrimaryBtn onClick={() => setSetupStep(3)} disabled={!qrCode}>
            I&apos;ve scanned it <ArrowRight size={17} />
          </PrimaryBtn>
        </div>
      )}

      {setupStep === 3 && (
        <CodeStep
          loading={loading}
          onVerify={onVerify}
          hint="Enter the 6-digit code shown in your app to finish setup."
          cta="Verify & continue"
        />
      )}
    </>
  );
}

/* ════════════════════════ screen 3 — verify ════════════════════════ */
function TotpVerify({ loading, onVerify, onBack }) {
  return (
    <>
      <BackLink onBack={onBack} />
      <H
        title="Two-factor authentication"
        sub="Enter the 6-digit code from Google Authenticator."
      />
      <CodeStep loading={loading} onVerify={onVerify} cta="Verify" />
    </>
  );
}

function CodeStep({ loading, onVerify, hint, cta }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const refs = useRef([]);
  const code = digits.join("");

  const set = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(-1);
    setDigits((d) => {
      const next = [...d];
      next[i] = clean;
      return next;
    });
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "Enter" && code.length === 6) {
      onVerify(code);
    }
  };

  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = ["", "", "", "", "", ""];
    txt.split("").forEach((c, idx) => (next[idx] = c));
    setDigits(next);
    refs.current[Math.min(txt.length, 5)]?.focus();
  };

  return (
    <div className="space-y-4">
      {hint && <p className="text-sm text-neutral-500">{hint}</p>}
      <div className="flex justify-between gap-2" onPaste={onPaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => set(i, e.target.value)}
            onKeyDown={(e) => onKeyDown(i, e)}
            className="h-14 w-full rounded-xl border border-neutral-200 bg-white text-center text-xl font-semibold text-neutral-900 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20"
          />
        ))}
      </div>
      <PrimaryBtn
        loading={loading}
        onClick={() => onVerify(code)}
        disabled={code.length !== 6}
      >
        {cta} <ShieldCheck size={17} />
      </PrimaryBtn>
    </div>
  );
}

/* ════════════════════════ screen 4 — forgot ════════════════════════ */
function Forgot({ email, setEmail, loading, forgotSent, handleForgot, onBack }) {
  if (forgotSent) {
    return (
      <>
        <BackLink onBack={onBack} />
        <div className="py-2 text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-emerald-100">
            <Mail size={22} className="text-emerald-600" />
          </div>
          <h2
            className="text-xl font-semibold text-neutral-900"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Check your inbox
          </h2>
          <p className="mt-2 text-sm text-neutral-500">
            If an account exists for {email}, we&apos;ve sent reset
            instructions.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="mt-6 text-sm font-medium text-violet-600 hover:text-violet-700"
          >
            Back to sign in
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <BackLink onBack={onBack} />
      <H
        title="Reset password"
        sub="We'll email you a link to set a new password."
      />
      <div className="space-y-3.5">
        <Field
          icon={Mail}
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleForgot()}
        />
        <PrimaryBtn loading={loading} onClick={handleForgot}>
          Send reset link <KeyRound size={17} />
        </PrimaryBtn>
      </div>
    </>
  );
}

/* ════════════════════════ small components ════════════════════════ */
function BackLink({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="mb-4 flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-neutral-900"
    >
      <ArrowLeft size={15} /> Back
    </button>
  );
}

function Steps({ active }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className={`h-1.5 flex-1 rounded-full transition ${
            n <= active ? "bg-violet-600" : "bg-neutral-200"
          }`}
        />
      ))}
    </div>
  );
}

function StoreCard({ label, sub }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5">
      <Smartphone size={18} className="text-violet-600" />
      <div className="leading-tight">
        <div className="text-sm font-medium text-neutral-900">{label}</div>
        <div className="text-xs text-neutral-500">{sub}</div>
      </div>
    </div>
  );
}